<template>
  <div class="editor-section">
    <!-- 模式選擇 -->
    <div class="mode-selector">
      <label class="mode-option" :class="{ active: mode === 'preset' }">
        <input type="radio" value="preset" v-model="mode" />
        <i class="ri-layout-3-line"></i>
        <div><span>預設版型</span><small>選擇版型並填入內容</small></div>
      </label>
      <label class="mode-option" :class="{ active: mode === 'custom' }">
        <input type="radio" value="custom" v-model="mode" />
        <i class="ri-code-s-slash-line"></i>
        <div><span>自訂 JSON</span><small>直接貼入 Flex JSON</small></div>
      </label>
    </div>

    <!-- 預設版型 -->
    <template v-if="mode === 'preset'">
      <div class="form-group">
        <label class="form-label required-label">選擇版型</label>
        <div class="preset-grid">
          <div
            v-for="p in presets"
            :key="p.key"
            class="preset-card"
            :class="{ selected: selectedPreset === p.key }"
            @click="selectPreset(p.key)"
          >
            <div class="preset-thumb">
              <div v-if="p.preview" class="preset-thumb-inner">
                <FlexBubble :bubble="p.preview" />
              </div>
              <div v-else class="preset-icon">
                <i :class="presetIcons[p.key] || 'ri-layout-line'"></i>
              </div>
            </div>
            <div class="preset-name">{{ p.name }}</div>
            <div class="preset-desc">{{ p.description }}</div>
          </div>
        </div>
      </div>

      <FlexPresetForm
        v-if="selectedPreset"
        :preset-key="selectedPreset"
        :schema="selectedPresetSchema"
        :modelValue="templateFields"
        @update:modelValue="updateTemplateFields"
      />
    </template>

    <!-- 自訂 JSON -->
    <template v-else>
      <div class="form-group">
        <label class="form-label required-label">
          Flex Message JSON
          <a href="https://developers.line.biz/flex-simulator/" target="_blank" class="help-link">
            <i class="ri-external-link-line"></i> Flex Simulator
          </a>
        </label>
        <textarea
          class="form-control code-textarea"
          :class="{ 'is-invalid': jsonError }"
          :value="flexJsonText"
          @input="handleJsonInput($event.target.value)"
          rows="14"
          placeholder='{"type":"bubble","body":{"type":"box","layout":"vertical","contents":[]}}'
        ></textarea>
        <span v-if="jsonError" class="error-text">{{ jsonError }}</span>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import FlexPresetForm from './FlexPresetForm.vue';
import FlexBubble from './FlexBubble.vue';
import { lineMsgTemplateService } from '@/api/modules/lineMsgTemplates.js';

const props = defineProps({ modelValue: { type: Object, required: true } });
const emit = defineEmits(['update:modelValue']);

const presets = ref([]);
/* 新建或未指定時預設「預設版型」；僅明確為 custom 才進自訂 JSON */
const mode = ref(props.modelValue.flexPreset === 'custom' ? 'custom' : 'preset');
const selectedPreset = ref(
  props.modelValue.flexPreset && props.modelValue.flexPreset !== 'custom' ? props.modelValue.flexPreset : ''
);
const templateFields = ref(props.modelValue.templateFields || {});
const flexJsonText = ref(
  props.modelValue.messageContent ? JSON.stringify(props.modelValue.messageContent, null, 2) : ''
);
const jsonError = ref('');

const presetIcons = {
  product_card: 'ri-shopping-bag-line',
  coupon_card: 'ri-coupon-3-line',
  event_announce: 'ri-calendar-event-line',
  order_confirm: 'ri-file-list-3-line',
  member_welcome: 'ri-user-heart-line',
  image_text: 'ri-image-2-line',
  image_banner: 'ri-gallery-line',
  template_card: 'ri-layout-4-line',
  business_card: 'ri-contacts-book-line',
  card_portrait_full: 'ri-id-card-line',
  card_avatar_center: 'ri-account-circle-line',
  card_company_hero: 'ri-building-2-line',
  card_gradient_banner: 'ri-color-filter-line',
  card_minimal_text: 'ri-text'
};

const selectedPresetSchema = computed(() => {
  const p = presets.value.find(x => x.key === selectedPreset.value);
  return p ? p.schema : [];
});

onMounted(async () => {
  try {
    const res = await lineMsgTemplateService.getPresets();
    if (res?.success && res.data) {
      presets.value = res.data;
      if (mode.value === 'preset' && !selectedPreset.value && presets.value.length) {
        selectPreset(presets.value[0].key);
      }
    }
  } catch (e) {
    console.error('載入預設版型失敗:', e);
  }
});

function schemaDefaults(schema) {
  const defaults = {};
  (schema || []).forEach((field) => {
    if (field.default !== undefined && field.default !== '') defaults[field.key] = field.default;
  });
  return defaults;
}

function selectPreset(key) {
  const changed = selectedPreset.value !== key;
  selectedPreset.value = key;
  // 換版型時不要沿用上一張的欄位，否則商品卡片的「立即購買」會留在名片上
  if (changed) {
    const preset = presets.value.find(item => item.key === key);
    templateFields.value = schemaDefaults(preset?.schema);
  }
  emitChange();
}

function updateTemplateFields(fields) {
  templateFields.value = fields;
  emitChange();
}

function handleJsonInput(text) {
  flexJsonText.value = text;
  jsonError.value = '';
  if (!text.trim()) {
    emit('update:modelValue', { ...props.modelValue, flexPreset: 'custom', messageContent: null });
    return;
  }
  try {
    const parsed = JSON.parse(text);
    jsonError.value = '';
    emit('update:modelValue', { ...props.modelValue, flexPreset: 'custom', messageContent: parsed, templateFields: null });
  } catch (e) {
    jsonError.value = 'JSON 格式錯誤：' + e.message;
  }
}

function emitChange() {
  emit('update:modelValue', {
    ...props.modelValue,
    flexPreset: selectedPreset.value,
    templateFields: templateFields.value
  });
}

watch(mode, (val) => {
  if (val === 'custom') {
    selectedPreset.value = '';
    emit('update:modelValue', { ...props.modelValue, flexPreset: 'custom', templateFields: null });
  } else if (val === 'preset' && !selectedPreset.value && presets.value.length) {
    selectPreset(presets.value[0].key);
  }
});
</script>

<style scoped>
.mode-selector { display: flex; gap: 12px; margin-bottom: 20px; }
.mode-option {
  flex: 1; display: flex; align-items: center; gap: 12px;
  padding: 14px 16px; border: 2px solid #e5e7eb; border-radius: 10px;
  cursor: pointer; transition: all .2s;
}
.mode-option input { display: none; }
.mode-option i { font-size: 24px; color: #9ca3af; }
.mode-option span { font-size: 14px; font-weight: 500; color: #374151; }
.mode-option small { font-size: 11px; color: #9ca3af; }
.mode-option div { display: flex; flex-direction: column; gap: 2px; }
.mode-option.active { border-color: #3b82f6; background: #eff6ff; }
.mode-option.active i { color: #3b82f6; }

.preset-grid {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px; margin-bottom: 20px;
}
.preset-card {
  padding: 10px; border: 2px solid #e5e7eb; border-radius: 10px;
  cursor: pointer; text-align: center; transition: all .2s;
}
.preset-card:hover { border-color: #93c5fd; }
.preset-card.selected { border-color: #3b82f6; background: #eff6ff; }

/* 版型示意圖：用實際編譯出的 bubble 縮小顯示，超出高度的部分淡出 */
.preset-thumb {
  position: relative; height: 132px; margin-bottom: 8px;
  overflow: hidden; border-radius: 8px; background: #f3f4f6;
  display: flex; align-items: flex-start; justify-content: center;
}
.preset-thumb-inner {
  width: 280px; flex-shrink: 0;
  transform: scale(.45); transform-origin: top center;
  pointer-events: none;
}
.preset-thumb::after {
  content: ''; position: absolute; left: 0; right: 0; bottom: 0; height: 26px;
  background: linear-gradient(rgba(243,244,246,0), rgba(243,244,246,.95));
}
.preset-icon { font-size: 28px; color: #6b7280; margin: auto; }
.preset-card.selected .preset-icon { color: #3b82f6; }
.preset-name { font-size: 13px; font-weight: 600; color: #374151; }
.preset-desc { font-size: 11px; color: #9ca3af; margin-top: 2px; }

.code-textarea { font-family: 'Courier New', monospace; font-size: 12px; resize: vertical; min-height: 200px; }
.help-link { font-size: 12px; margin-left: 8px; color: #3b82f6; text-decoration: none; }
.error-text { display: block; font-size: 12px; color: #ef4444; margin-top: 4px; }
</style>
